import type { StateMachine } from './machine';

/** Quote state machine — core-data-model §3.3. Conversion is a LINK (converted_*_id),
 *  not a state — an approved quote stays `approved`. `quote.revise` clones the row as a
 *  new revision and marks the old one `superseded`. */
export const QUOTE_STATES = [
  'draft',
  'sent',
  'approved',
  'declined',
  'expired',
  'superseded',
] as const;
export type QuoteState = (typeof QUOTE_STATES)[number];

export const quoteMachine: StateMachine<QuoteState> = {
  states: QUOTE_STATES,
  initial: 'draft',
  terminal: ['approved', 'declined', 'superseded'],
  transitions: [
    { from: 'draft', to: 'sent', via: 'quote.send' },
    /**
     * Chasing it. "Did you get my quote?" is the most ordinary thing an office does, and the
     * button on the quote page says "Send again" — which threw
     * `Illegal quote transition sent → (none) via quote.send` at whoever pressed it, because
     * this edge didn't exist. Same shape as the missing `partially_refunded` self-loop.
     */
    { from: 'sent', to: 'sent', via: 'quote.send' },
    { from: 'draft', to: 'approved', via: 'quote.approve' }, // office records verbal OK
    { from: 'sent', to: 'approved', via: 'quote.approve' }, // portal / office
    { from: 'sent', to: 'declined', via: 'quote.decline' },
    { from: 'sent', to: 'expired', via: 'quote.expire', system: true },
    /**
     * The way back. This was `via: 'quote.resend'` — an edge with NO caller anywhere in the
     * codebase, while the office's actual button calls `quote.send`. Harmless until the
     * auto-archive automation started expiring quotes on a timer, at which point it became a
     * one-way door: the shop opens the filed-away quote, presses "Send again", and gets
     * `Illegal quote transition expired → (none) via quote.send`.
     *
     * A second name for a transition nothing invokes is not documentation, it is a trap.
     */
    { from: 'expired', to: 'sent', via: 'quote.send' },
    { from: 'draft', to: 'superseded', via: 'quote.revise' },
    { from: 'sent', to: 'superseded', via: 'quote.revise' },
  ],
};
